/**
 * The agency's members as a light spreadsheet.
 *
 * Every row is a member and every column a fact about them. Names, phone,
 * titles and Roger-employee status are edited in the cell; each commit is one
 * save of one field, shown at once and put back with a message if the save
 * fails. Agency roles, app accounts and licenses open dialogs of their own,
 * and the row menu holds the account actions (resend, cancel invite,
 * deactivate).
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Button,
  FilterToolbarSearch,
  cn,
  filterToolbarSearchGrowClassName,
} from '@roger/ui-components';
import { Loader2, Mail, UserPlus } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import {
  type HeaderFilterConfig,
  Spreadsheet,
  type SpreadsheetHandle,
  useFillHeight,
} from '../../spreadsheet';
import {
  bulkAssignAgencyRolesToUser,
  countUserLicenses,
  inviteUser,
  listUserRoleAssignments,
  updateClinicSetup,
  updateUser,
  updateUserCredentials,
  updateUserType,
} from '../../../services/api';
import { getBusinessErrorMessage } from '../../../utils/apiErrorDetail';
import { getHttpStatus } from '../../../utils/httpErrorUtils';
import type { UserType } from '../../../services/api/models/UserType';
import type { RogerDocClinicianAccount } from '../../../services/accountLinkApi';
import { FeatureFlag, PermissionSlug } from '../../../constants/featureFlags';
import { useStore } from '../../../store';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { useShowEmrUserManagement } from '../../../hooks/useShowEmrUserManagement';
import { formatUsPhoneEntry, isCompleteUsPhone } from '../../../utils/intake';
import { filterUserVisibleRoles } from '../../../utils/rbacRoles';
import {
  DEFAULT_VIEW_STATUS_FILTER,
  type MemberView,
  canEditProfile,
  isAgencyMember,
  memberStatus,
  memberStatusOptions,
  memberView,
} from '../memberStatus';
import type {
  AgencyClinicResponse,
  MemberStatus,
  OrgMemberResponse,
} from '../types';
import {
  type LicensesColumnContext,
  memberColumns,
  type RowAction,
} from './memberColumns';
import {
  type ClinicRoleIds,
  type ClinicSetupEntry,
  type Employment,
  clinicSetupBody,
  heldThrough,
  memberDisplayName,
  memberWithEntries,
  sameSetup,
  setupEntries,
  withEmployment,
  withRoles,
} from './clinicSetup';
import { useClinicSetupData } from './useClinicSetupData';
import { ClinicSetupDialog } from './ClinicSetupDialog';
import { MemberAgencyRolesDialog } from './MemberAgencyRolesDialog';
import { MemberAppAccountsDialog } from './MemberAppAccountsDialog';
import { MemberLicensesDialog } from './MemberLicensesDialog';
import { GridConfirm } from './GridConfirm';
import { ColumnChooser, SaveStatus, ViewToggle } from './toolbar';

export type MembersGridProps = {
  /** The members the page's search and status filter leave. */
  members: OrgMemberResponse[];
  /** Every loaded member: the role filter's options, and somebody just added. */
  allMembers: OrgMemberResponse[];
  /** The search matched people outside the ticked statuses, so they show too. */
  showingOutsideFilter: boolean;
  isLoading: boolean;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  statusFilter: MemberStatus[];
  onStatusFilterChange: (value: MemberStatus[]) => void;
  onInvite: () => void;
  /** The page's export control, placed with the other toolbar actions. */
  exportControl?: React.ReactNode;
  /** Opens the account merge keeping that row's member; absent, rows don't offer it. */
  onMergeAccounts?: (keep: OrgMemberResponse) => void;
  /** Somebody just added: the grid moves to their row once it loads. */
  revealEmail?: { email: string } | null;
  canInvite: boolean;
  /** Names, phone and titles: super-user or `clinician-mgr:access`. */
  canEditUserInfo: boolean;
  canEditRogerEmployee: boolean;
  /** Clinics, clinic roles, disciplines and employee type: `clinician-mgr:access`. */
  canEditClinicSetup: boolean;
  /** Reload the members after a save, so the grid shows the server's values. */
  onMemberChanged: () => void;
  rogerDocLinksByEmail: Record<string, RogerDocClinicianAccount[]>;
  rogerDocAccountsLoaded: boolean;
  /** Roger Docs could not be asked, so the App accounts cells say so. */
  rogerDocAccountsFailed?: boolean;
  /** Every Roger App account in the agency, once the page has loaded them. */
  rogerDocAccounts?: RogerDocClinicianAccount[];
  /** The account list after a link, unlink or new account in the grid. */
  onRogerDocAccountsChange: (accounts: RogerDocClinicianAccount[]) => void;
  /** Reloads the account list, for a dialog call that ends after it closes. */
  onRogerDocLinksChanged: () => void;
  clinicNamesById?: ReadonlyMap<string, string>;
  /** Clinics the clinic filter offers. */
  clinics: AgencyClinicResponse[];
  canDeactivate: boolean;
  canCancelInvite: boolean;
  onResendInvite: (member: OrgMemberResponse) => Promise<void> | void;
  onToggleActivation?: (member: OrgMemberResponse) => Promise<void> | void;
  onRetryDeactivation?: (member: OrgMemberResponse) => Promise<void> | void;
  onCancelInvite?: (member: OrgMemberResponse) => Promise<void> | void;
  currentUserId?: string;
};

// Remembered per browser: a convenience, not something anybody else needs.
const HIDDEN_COLUMNS_KEY = 'user-manager-grid:hidden-columns:v1';
const VIEW_KEY = 'user-manager-grid:view:v1';
/** Hidden until somebody chooses otherwise: an occasional column, a click away. */
const DEFAULT_HIDDEN_COLUMNS = ['roger_employee'];
/** Pinned: whose row it is, and what can be done to it. */
const UNHIDEABLE_COLUMNS = new Set(['first_name', 'last_name', 'actions']);
/** The invite confirmations' caution: an invite hands over what is set up now. */
const INVITE_WARNING =
  'Check their roles and clinics first; they can use them as soon as they accept.';
/** People per invite request, so a long list is not one slow request. */
const INVITE_BATCH = 100;
const INITIAL_SORTING = [
  { id: 'status', desc: false },
  { id: 'last_name', desc: false },
];

function loadView(): MemberView {
  try {
    return localStorage.getItem(VIEW_KEY) === 'not_invited'
      ? 'not_invited'
      : 'team';
  } catch {
    return 'team';
  }
}

function loadHiddenColumns(): string[] {
  try {
    const saved = localStorage.getItem(HIDDEN_COLUMNS_KEY);
    return saved ? (JSON.parse(saved) as string[]) : DEFAULT_HIDDEN_COLUMNS;
  } catch {
    return DEFAULT_HIDDEN_COLUMNS;
  }
}

/**
 * Edits made in the grid, laid over the loaded members while they save and
 * until the reload after them lands.
 */
type Overrides = Map<string, Partial<OrgMemberResponse>>;
type Fields = Record<string, unknown>;

/** Where a save waits its turn: one place per member and set of fields. */
const queueKey = (userId: string, patch: Partial<OrgMemberResponse>) =>
  `${userId}:${Object.keys(patch).join(',')}`;

/** A clinic-setup save's place in the queue, shared by every clinic edit. */
const clinicSetupKey = (userId: string) => `${userId}:clinic-setup`;

/** One running row action: resends by email, the rest by Roger user. */
const busyKey = (action: RowAction, member: OrgMemberResponse) =>
  `${action}:${action === 'resend' ? member.email : member.roger_user_id}`;

export const MembersGrid: React.FC<MembersGridProps> = ({
  members,
  allMembers,
  showingOutsideFilter,
  isLoading,
  searchQuery,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  onInvite,
  exportControl,
  onMergeAccounts,
  revealEmail,
  canInvite,
  canEditUserInfo,
  canEditRogerEmployee,
  canEditClinicSetup,
  onMemberChanged,
  rogerDocLinksByEmail,
  rogerDocAccountsLoaded,
  rogerDocAccountsFailed = false,
  rogerDocAccounts,
  onRogerDocAccountsChange,
  onRogerDocLinksChanged,
  clinicNamesById,
  clinics,
  canDeactivate,
  canCancelInvite,
  onResendInvite,
  onToggleActivation,
  onRetryDeactivation,
  onCancelInvite,
  currentUserId,
}) => {
  const { showError, showSuccess, hasFeatureFlag, hasPermission } = useStore(
    useShallow(state => ({
      showError: state.showError,
      showSuccess: state.showSuccess,
      hasFeatureFlag: state.hasFeatureFlag,
      hasPermission: state.hasPermission,
    }))
  );
  // Titles and clinics are EMR agencies only; clinic roles and disciplines
  // also need the clinic-info flag.
  const showEmr = useShowEmrUserManagement();
  const showClinicRoles =
    hasFeatureFlag(FeatureFlag.SHOW_CLINIC_INFO_IN_USER_MANAGER) && showEmr;
  /** The member whose Roger App accounts are open, if any. */
  const [appAccountsOpenFor, setAppAccountsOpenFor] = useState<string | null>(
    null
  );

  // ── Licenses ───────────────────────────────────────────────────────────
  const canViewLicenses =
    hasPermission(PermissionSlug.USER_LICENSES_VIEW) ||
    hasPermission(PermissionSlug.USER_LICENSES_EDIT);
  const canEditLicenses = hasPermission(PermissionSlug.USER_LICENSES_EDIT);
  const [licenseCounts, setLicenseCounts] = useState<Record<
    string,
    number
  > | null>(null);
  const [licenseCountsFailed, setLicenseCountsFailed] = useState(false);
  const loadLicenseCounts = useCallback(async () => {
    try {
      const response = await countUserLicenses({ throwOnError: true });
      setLicenseCounts(response.data);
      setLicenseCountsFailed(false);
    } catch (error) {
      console.error('Failed to load license counts:', error);
      setLicenseCountsFailed(true);
    }
  }, []);
  // Again with every members reload: an account merge moves licenses.
  useEffect(() => {
    if (canViewLicenses) void loadLicenseCounts();
  }, [canViewLicenses, loadLicenseCounts, allMembers]);
  /** The workos id of the member whose licenses are open. */
  const [licensesOpenFor, setLicensesOpenFor] = useState<string | null>(null);
  const licensesColumn = useMemo<LicensesColumnContext | null>(
    () =>
      canViewLicenses
        ? {
            counts: licenseCounts,
            failed: licenseCountsFailed,
            open: member => setLicensesOpenFor(member.workos_user_id),
          }
        : null,
    [canViewLicenses, licenseCounts, licenseCountsFailed]
  );

  // ── Saving indicator ───────────────────────────────────────────────────
  // Every write the grid makes goes through trackSave, so the toolbar can say
  // "Saving…" while any is in flight and "Saved" once they all land.
  const [savesInFlight, setSavesInFlight] = useState(0);
  // Set by a failed save and cleared by the next one that lands, so the
  // toolbar never says "Saved" over a change that was put back. Set in the
  // same callback as the count, so both land in one render.
  const [lastSaveFailed, setLastSaveFailed] = useState(false);
  const trackSave = useCallback(<R,>(promise: Promise<R>): Promise<R> => {
    setSavesInFlight(n => n + 1);
    return promise.then(
      value => {
        setLastSaveFailed(false);
        setSavesInFlight(n => n - 1);
        return value;
      },
      (error: unknown) => {
        setLastSaveFailed(true);
        setSavesInFlight(n => n - 1);
        throw error;
      }
    );
  }, []);

  const [overrides, setOverrides] = useState<Overrides>(new Map());
  // The saves read the overlay through this ref, so they keep their identity
  // and the columns are not rebuilt on every save.
  const overridesRef = useRef<Overrides>(overrides);
  overridesRef.current = overrides;
  /** Saves still running, per member and field ("userId:field"). */
  const inFlightRef = useRef(new Map<string, number>());
  useEffect(() => {
    // A fresh load carries every finished save, so only the fields still
    // saving stay laid over it.
    setOverrides(prev => {
      const next: Overrides = new Map();
      for (const [userId, fields] of prev) {
        const kept = Object.fromEntries(
          Object.entries(fields).filter(
            ([field]) =>
              (inFlightRef.current.get(`${userId}:${field}`) ?? 0) > 0
          )
        );
        if (Object.keys(kept).length) {
          next.set(userId, kept as Partial<OrgMemberResponse>);
        }
      }
      return next;
    });
  }, [allMembers]);

  /**
   * Shows `patch` at once and runs `request`. A failure puts back only this
   * save's fields, and only where they still hold its values, so a later edit
   * to the same person survives it.
   */
  const save = useCallback(
    async (
      userId: string,
      patch: Partial<OrgMemberResponse>,
      request: () => Promise<unknown>
    ) => {
      const fields = Object.keys(patch);
      const before: Fields = { ...(overridesRef.current.get(userId) ?? {}) };
      const flight = inFlightRef.current;
      for (const field of fields) {
        const key = `${userId}:${field}`;
        flight.set(key, (flight.get(key) ?? 0) + 1);
      }
      setOverrides(prev => {
        const next = new Map(prev);
        next.set(userId, { ...(prev.get(userId) ?? {}), ...patch });
        return next;
      });
      try {
        await trackSave(request());
        onMemberChanged();
      } catch (error) {
        console.error('Failed to save the member:', error);
        // A 409 says why, in words meant for the person who made the change.
        showError(
          (getHttpStatus(error) === 409 && getBusinessErrorMessage(error)) ||
            'That change could not be saved. Please try again.'
        );
        const saved: Fields = patch;
        setOverrides(prev => {
          const current: Fields = { ...(prev.get(userId) ?? {}) };
          for (const field of fields) {
            if (current[field] !== saved[field]) continue;
            // The value before this save is only worth showing while the save
            // that set it is still running; otherwise fall back to the load.
            const others = (flight.get(`${userId}:${field}`) ?? 1) - 1;
            if (others > 0 && field in before) current[field] = before[field];
            else delete current[field];
          }
          const next = new Map(prev);
          if (Object.keys(current).length) {
            next.set(userId, current as Partial<OrgMemberResponse>);
          } else {
            next.delete(userId);
          }
          return next;
        });
      } finally {
        for (const field of fields) {
          const key = `${userId}:${field}`;
          flight.set(key, (flight.get(key) ?? 1) - 1);
        }
      }
    },
    [onMemberChanged, showError, trackSave]
  );

  /**
   * Saves waiting their turn, per member and field. Each save sends the
   * field's whole value, so one runs at a time, and while one is running only
   * the latest waits: an older value must never land after a newer one.
   * A save that sends only a change, not the whole value, passes `chain` so
   * every waiting one is sent, in order.
   */
  const queuedRef = useRef(new Map<string, (() => Promise<void>) | null>());
  const queueSave = useCallback(
    async (
      userId: string,
      patch: Partial<OrgMemberResponse>,
      request: () => Promise<unknown>,
      key = queueKey(userId, patch),
      chain = false
    ) => {
      const run = () => save(userId, patch, request);
      const queued = queuedRef.current;
      if (queued.has(key)) {
        // One is running: show this value now, send it when that one is done.
        const waiting = chain ? queued.get(key) : null;
        queued.set(
          key,
          waiting
            ? async () => {
                await waiting();
                await run();
              }
            : run
        );
        setOverrides(prev => {
          const next = new Map(prev);
          next.set(userId, { ...(prev.get(userId) ?? {}), ...patch });
          return next;
        });
        return;
      }
      queued.set(key, null);
      let next: (() => Promise<void>) | null = run;
      try {
        while (next) {
          await next();
          next = queued.get(key) ?? null;
          queued.set(key, null);
        }
      } finally {
        queued.delete(key);
      }
    },
    [save]
  );

  const saveField = useCallback(
    async (member: OrgMemberResponse, patch: Partial<OrgMemberResponse>) => {
      const userId = member.roger_user_id;
      if (!userId) return;
      // Names cannot be blank; the cell snaps back to its value.
      if (patch.first_name !== undefined && !patch.first_name?.trim()) {
        showError('First name is required');
        return;
      }
      if (patch.last_name !== undefined && !patch.last_name?.trim()) {
        showError('Last name is required');
        return;
      }
      let phone = patch.phone_number;
      if (phone !== undefined) {
        const entered = phone?.trim();
        phone = entered ? formatUsPhoneEntry(entered) : null;
        // Text with no digits formats to nothing; only a blank entry clears.
        if ((entered && !phone) || !isCompleteUsPhone(phone)) {
          showError('Enter a 10-digit US phone number');
          return;
        }
      }
      const checked: Partial<OrgMemberResponse> =
        phone !== undefined ? { ...patch, phone_number: phone || null } : patch;
      // Only the field that changed: the others are undefined, which the
      // request leaves out.
      const { first_name, last_name, phone_number, is_roger_employee } =
        checked;
      await queueSave(userId, checked, () =>
        updateUser({
          path: { user_id: userId },
          body: { first_name, last_name, phone_number, is_roger_employee },
          throwOnError: true,
        })
      );
    },
    [queueSave, showError]
  );

  const saveTitles = useCallback(
    async (member: OrgMemberResponse, userTypes: UserType[]) => {
      const userId = member.roger_user_id;
      if (!userId) return;
      await queueSave(userId, { user_types: userTypes }, () =>
        updateUserType({
          path: { user_id: userId },
          body: { user_types: userTypes },
          throwOnError: true,
        })
      );
    },
    [queueSave]
  );

  const saveCredentials = useCallback(
    async (member: OrgMemberResponse, credentials: string) => {
      const userId = member.roger_user_id;
      if (!userId) return;
      const trimmed = credentials.trim() || null;
      if (trimmed && trimmed.length > 100) {
        showError('Credentials must be 100 characters or fewer');
        return;
      }
      await queueSave(userId, { credentials: trimmed }, () =>
        updateUserCredentials({
          path: { user_id: userId },
          body: { credentials: trimmed },
          throwOnError: true,
        })
      );
    },
    [queueSave, showError]
  );

  // ── Clinic setup ───────────────────────────────────────────────────────
  const editClinicSetup = showEmr && canEditClinicSetup;
  const { boundClinics, catalogue, roleIds } = useClinicSetupData(
    editClinicSetup,
    allMembers,
    showError
  );

  const boundIds = useMemo(
    () => new Set((boundClinics ?? []).map(c => c.id)),
    [boundClinics]
  );
  const boundClinicOptions = useMemo(
    () =>
      (boundClinics ?? [])
        .map(c => ({ value: c.id, label: c.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [boundClinics]
  );
  const clinicName = useCallback(
    (id: string) =>
      clinicNamesById?.get(id) ??
      boundClinics?.find(c => c.id === id)?.name ??
      clinics.find(c => c.id === id)?.name ??
      'a clinic',
    [clinicNamesById, boundClinics, clinics]
  );
  const flagHeldThrough = useCallback(
    (entry: ClinicSetupEntry, key: keyof ClinicRoleIds) =>
      roleIds && catalogue ? heldThrough(entry, key, roleIds, catalogue) : null,
    [roleIds, catalogue]
  );

  // ── Agency roles ───────────────────────────────────────────────────────
  const roleById = useMemo(
    () => (catalogue ? new Map(catalogue.map(role => [role.id, role])) : null),
    [catalogue]
  );
  /** The agency roles the editor offers: agency-scoped, minus hidden ones. */
  const offeredAgencyRoles = useMemo(() => {
    if (!catalogue) return null;
    return filterUserVisibleRoles(
      catalogue.filter(role => role.scope === 'agency')
    );
  }, [catalogue]);
  /**
   * Whose agency roles the editor is open for, and their ticks as it opened.
   * The save compares against those ticks, not the row's, so a role granted
   * by somebody else while it is open is not read as unticked and revoked.
   */
  const [agencyRolesOpenFor, setAgencyRolesOpenFor] = useState<{
    userId: string;
    before: ReadonlySet<string>;
  } | null>(null);

  /** The offered roles a member holds: the editor's ticks as it opens. */
  const ticked = useCallback(
    (member: OrgMemberResponse): ReadonlySet<string> => {
      const offered = new Set((offeredAgencyRoles ?? []).map(role => role.id));
      return new Set(
        (member.role_assignments ?? [])
          .map(role => role.id)
          .filter(id => offered.has(id))
      );
    },
    [offeredAgencyRoles]
  );

  /**
   * Saves what the operator changed in the editor, and only that. The route
   * replaces every agency role, so the member's roles are read fresh and the
   * change applied to them: anything the grid leaves out (hidden,
   * internal, off-product roles) goes back as it was, even where the editor
   * offers it.
   */
  const saveAgencyRoles = useCallback(
    async (
      member: OrgMemberResponse,
      before: ReadonlySet<string>,
      after: readonly string[]
    ) => {
      const userId = member.roger_user_id;
      if (!userId || !offeredAgencyRoles) return;
      const added = offeredAgencyRoles.filter(
        role => after.includes(role.id) && !before.has(role.id)
      );
      const removed = new Set([...before].filter(id => !after.includes(id)));
      const shown = [
        ...(member.role_assignments ?? []).filter(
          role => !removed.has(role.id)
        ),
        ...added.map(({ id, slug, name, description }) => ({
          id,
          slug,
          name,
          description,
        })),
      ];
      await queueSave(
        userId,
        { role_assignments: shown },
        async () => {
          const { data: held } = await listUserRoleAssignments({
            path: { user_id: userId },
            // The default page would leave older roles out, and the save would
            // revoke them.
            query: { limit: 5000 },
            throwOnError: true,
          });
          const kept = held
            .filter(a => a.clinic_id == null && !removed.has(a.role_id))
            .map(a => a.role_id);
          await bulkAssignAgencyRolesToUser({
            path: { user_id: userId },
            body: {
              role_ids: [...new Set([...kept, ...added.map(role => role.id)])],
            },
            throwOnError: true,
          });
        },
        undefined,
        // Each save sends only what changed, so none may replace another.
        true
      );
    },
    [offeredAgencyRoles, queueSave]
  );

  // The members as loaded, for a save that runs after the props moved on.
  const allMembersRef = useRef(allMembers);
  allMembersRef.current = allMembers;
  /** Per member: the clinic setup the server last confirmed this session. */
  const confirmedRef = useRef(new Map<string, ClinicSetupEntry[]>());
  /** Per member: the setup the operator wants, sent by the next save. */
  const desiredRef = useRef(new Map<string, ClinicSetupEntry[]>());
  useEffect(() => {
    // A fresh load is the server's word, except where a save is still going.
    for (const userId of [...confirmedRef.current.keys()]) {
      if (!queuedRef.current.has(clinicSetupKey(userId))) {
        confirmedRef.current.delete(userId);
      }
    }
  }, [allMembers]);

  /**
   * Saves a member's whole clinic setup in one request. All four clinic
   * columns and the per-clinic editor share one place in the queue, so their
   * edits go one at a time and the latest wins. The body is built when the
   * request goes, from the latest wanted setup against the setup the server
   * last confirmed, so an edit undone while another saves is still sent.
   */
  const saveClinicSetup = useCallback(
    async (member: OrgMemberResponse, entries: ClinicSetupEntry[]) => {
      const userId = member.roger_user_id;
      if (!userId) return;
      const confirmed = () => {
        const loaded =
          allMembersRef.current.find(
            m => m.workos_user_id === member.workos_user_id
          ) ?? member;
        return {
          loaded,
          entries:
            confirmedRef.current.get(userId) ?? setupEntries(loaded, boundIds),
        };
      };
      const patch = memberWithEntries(member, entries, boundIds, clinicName);
      desiredRef.current.set(userId, entries);
      const key = clinicSetupKey(userId);
      await queueSave(
        userId,
        patch,
        async () => {
          const desired = desiredRef.current.get(userId) ?? entries;
          const base = confirmed();
          try {
            const { data } = await updateClinicSetup({
              path: { user_id: userId },
              body: { clinics: clinicSetupBody(base.entries, desired) },
              throwOnError: true,
            });
            confirmedRef.current.set(
              userId,
              data?.clinic_access ? setupEntries(data, boundIds) : desired
            );
            return data;
          } catch (error) {
            // Edits queued behind this one were made on top of it, so they go
            // too, and the grid shows the setup the server still has.
            if (queuedRef.current.has(key)) queuedRef.current.set(key, null);
            desiredRef.current.delete(userId);
            const restored = memberWithEntries(
              base.loaded,
              base.entries,
              boundIds,
              clinicName
            );
            setOverrides(prev => {
              const next = new Map(prev);
              next.set(userId, { ...(prev.get(userId) ?? {}), ...restored });
              return next;
            });
            throw error;
          }
        },
        key
      );
    },
    [boundIds, clinicName, queueSave]
  );
  /**
   * The member whose per-clinic editor is open, and an employee type it opens
   * with at every clinic ("Other", whose text it asks for).
   */
  const [setupOpenFor, setSetupOpenFor] = useState<{
    userId: string;
    employment?: Employment;
  } | null>(null);
  /**
   * An edit that removes access or roles, or sends an invitation, waiting to
   * be confirmed. A `run` that returns a promise keeps the dialog open, its
   * buttons waiting, until the request is done.
   */
  const [pendingClear, setPendingClear] = useState<{
    title: string;
    message: string;
    warning?: string;
    confirmLabel: string;
    run: () => void | Promise<void>;
  } | null>(null);
  const [confirmRunning, setConfirmRunning] = useState(false);

  /**
   * A clinic-setup edit from a cell or the per-clinic editor. One that takes a
   * clinic away, or every clinic role, asks first, as Delete does; one that
   * changes nothing sends nothing.
   */
  const requestClinicSetup = useCallback(
    (member: OrgMemberResponse, entries: ClinicSetupEntry[]) => {
      const current = setupEntries(member, boundIds);
      if (sameSetup(current, entries)) return;
      const run = () => void saveClinicSetup(member, entries);
      const name = memberDisplayName(member);
      const removed = current.filter(
        e => !entries.some(next => next.clinic_id === e.clinic_id)
      );
      const anyRole = (list: ClinicSetupEntry[]) =>
        list.some(e => e.clinician || e.caseManager || e.clinicalManager);
      if (removed.length) {
        setPendingClear(
          entries.length
            ? {
                title: 'Remove clinics',
                message: `Remove ${name} from ${removed.map(e => clinicName(e.clinic_id)).join(', ')}?`,
                confirmLabel: 'Remove',
                run,
              }
            : {
                title: 'Clear clinics',
                message: `Remove ${name} from every clinic you manage?`,
                confirmLabel: 'Clear',
                run,
              }
        );
        return;
      }
      // Clinics where they are a clinician before and after: what the
      // Disciplines column shows. Losing Clinician is a roles edit, asked
      // about (if at all) as one.
      const stillClinician = current.flatMap(before => {
        const after = entries.find(e => e.clinic_id === before.clinic_id);
        return before.clinician && after?.clinician ? [{ before, after }] : [];
      });
      if (
        stillClinician.some(({ before }) => before.disciplines.length > 0) &&
        stillClinician.every(({ after }) => after.disciplines.length === 0)
      ) {
        setPendingClear({
          title: 'Clear disciplines',
          message: `Take ${name}'s disciplines away at every clinic you manage?`,
          confirmLabel: 'Clear',
          run,
        });
        return;
      }
      if (anyRole(current) && !anyRole(entries)) {
        setPendingClear({
          title: 'Clear clinic roles',
          message: `Take ${name}'s clinic roles away at every clinic you manage?`,
          confirmLabel: 'Clear',
          run,
        });
        return;
      }
      run();
    },
    [boundIds, clinicName, saveClinicSetup]
  );

  // ── Row actions ────────────────────────────────────────────────────────
  // Per member, so a slow request is not sent
  // twice and two rows stay independent.
  const [busyActions, setBusyActions] = useState<ReadonlySet<string>>(
    new Set()
  );
  const isBusy = useCallback(
    (action: RowAction, member: OrgMemberResponse) =>
      busyActions.has(busyKey(action, member)),
    [busyActions]
  );
  const guarded = useCallback(
    (
      action: RowAction,
      handler: ((member: OrgMemberResponse) => Promise<void> | void) | undefined
    ) =>
      handler &&
      ((member: OrgMemberResponse) => {
        const key = busyKey(action, member);
        setBusyActions(prev => new Set(prev).add(key));
        void Promise.resolve(handler(member)).finally(() =>
          setBusyActions(prev => {
            const next = new Set(prev);
            next.delete(key);
            return next;
          })
        );
      }),
    []
  );

  // ── Members not invited yet ────────────────────────────────────────────
  // Kinnser staff the sync brought in. Inviting is the ordinary invite: it
  // makes them a member and sends the invitation, and adds no clinics beyond
  // the ones Kinnser gave them.
  const invite = useCallback(
    async (people: OrgMemberResponse[]) => {
      const emails = people.map(m => m.email).filter(Boolean);
      const invited: string[] = [];
      const emailFailed: string[] = [];
      const failed: string[] = [];
      try {
        for (let i = 0; i < emails.length; i += INVITE_BATCH) {
          const res = await trackSave(
            inviteUser({
              body: {
                emails: emails.slice(i, i + INVITE_BATCH),
                clinic_ids: [],
              },
              throwOnError: true,
            })
          );
          for (const result of res.data.results) {
            if (!result.success) failed.push(result.email);
            else if (result.email_failed) emailFailed.push(result.email);
            else invited.push(result.email);
          }
        }
      } catch (error) {
        console.error('Failed to send the invitations:', error);
        showError(
          getBusinessErrorMessage(error) ??
            'The invitations could not be sent. Please try again.'
        );
      } finally {
        onMemberChanged();
      }
      const names = (list: string[]) => {
        const named = list.slice(0, 3).map(email => {
          const m = people.find(
            p => p.email.toLowerCase() === email.toLowerCase()
          );
          return m ? memberDisplayName(m) : email;
        });
        const more = list.length > 3 ? ` and ${list.length - 3} more` : '';
        return `${named.join(', ')}${more}`;
      };
      if (invited.length) {
        showSuccess(
          invited.length === 1
            ? `Invited ${names(invited)}`
            : `Sent ${invited.length} invites`
        );
      }
      if (emailFailed.length) {
        showError(
          `Invited ${names(emailFailed)}, but the email did not send. Use Resend invite on their row.`
        );
      }
      if (failed.length) {
        showError(`Could not invite ${names(failed)}. Please try again.`);
      }
    },
    [trackSave, showSuccess, showError, onMemberChanged]
  );

  const sendInvite = useCallback(
    (member: OrgMemberResponse) => {
      if (!member.email) return;
      setPendingClear({
        title: 'Send invite',
        message: `Email ${member.email} an invitation to sign in to Roger?`,
        warning: INVITE_WARNING,
        confirmLabel: 'Send invite',
        run: () => invite([member]),
      });
    },
    [invite]
  );

  const rowActions = useMemo(
    () => ({
      onResendInvite: guarded('resend', onResendInvite) ?? (() => {}),
      onRetryDeactivation: guarded('retry', onRetryDeactivation),
      onToggleActivation: guarded('activation', onToggleActivation),
      onCancelInvite: guarded('cancel', onCancelInvite),
      onSendInvite: sendInvite,
    }),
    [
      guarded,
      onResendInvite,
      onRetryDeactivation,
      onToggleActivation,
      onCancelInvite,
      sendInvite,
    ]
  );

  // ── Filters ────────────────────────────────────────────────────────────
  const [agencyRoleFilter, setAgencyRoleFilter] = useState<string[]>([]);
  const [clinicFilter, setClinicFilter] = useState<string[]>([]);
  const agencyRoleOptions = useMemo(() => {
    const byId = new Map<string, string>();
    for (const m of allMembers) {
      for (const role of filterUserVisibleRoles(m.role_assignments ?? [])) {
        byId.set(role.id, role.name);
      }
    }
    return [...byId]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allMembers]);
  const clinicOptions = useMemo(
    () =>
      clinics
        .map(c => ({ value: c.id, label: c.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [clinics]
  );

  // ── Team or not invited ────────────────────────────────────────────────
  // Two jobs, never on screen together. Each keeps its own status ticks, so
  // switching back finds them as they were left.
  const [view, setViewState] = useState<MemberView>(loadView);
  const statusByViewRef = useRef<Partial<Record<MemberView, MemberStatus[]>>>(
    {}
  );
  const switchView = useCallback(
    (next: MemberView, alsoShow?: MemberStatus) => {
      statusByViewRef.current[view] = statusFilter;
      const restored = statusByViewRef.current[next] ?? [
        ...DEFAULT_VIEW_STATUS_FILTER[next],
      ];
      onStatusFilterChange(
        alsoShow && !restored.includes(alsoShow)
          ? [...restored, alsoShow]
          : restored
      );
      setViewState(next);
      try {
        localStorage.setItem(VIEW_KEY, next);
      } catch {
        // Private mode or blocked storage: remembered for this visit only.
      }
    },
    [view, statusFilter, onStatusFilterChange]
  );
  // The page's filter starts out as its own default; the grid opens on its
  // view's own ticks instead.
  const viewFilterSetRef = useRef(false);
  useEffect(() => {
    if (viewFilterSetRef.current) return;
    viewFilterSetRef.current = true;
    onStatusFilterChange([...DEFAULT_VIEW_STATUS_FILTER[view]]);
  }, [view, onStatusFilterChange]);

  const viewCounts = useMemo(() => {
    const counts = { team: 0, not_invited: 0 };
    for (const m of allMembers) {
      const status = memberStatus(m);
      // Who each job is about: the deactivated are a click away in the
      // status filter, not part of the count.
      if (status === 'not_invited') counts.not_invited += 1;
      else if (status !== 'deactivated') {
        counts.team += 1;
      }
    }
    return counts;
  }, [allMembers]);

  const rows = useMemo(() => {
    return members
      .map(member => {
        const override = member.roger_user_id
          ? overrides.get(member.roger_user_id)
          : undefined;
        return override ? { ...member, ...override } : member;
      })
      .filter(m => {
        if (memberView(m) !== view) return false;
        // Within a filter any ticked value matches; across filters, all must.
        if (
          agencyRoleFilter.length &&
          !(m.role_assignments ?? []).some(r => agencyRoleFilter.includes(r.id))
        ) {
          return false;
        }
        if (
          showEmr &&
          clinicFilter.length &&
          !(m.clinic_access ?? []).some(c => clinicFilter.includes(c.clinic_id))
        ) {
          return false;
        }
        return true;
      });
  }, [members, overrides, view, agencyRoleFilter, clinicFilter, showEmr]);

  // A search that only matches people in the other view says so, rather than
  // showing an empty list. The page widens a search past the ticked statuses,
  // so those people are in `members`.
  const otherView: MemberView = view === 'team' ? 'not_invited' : 'team';
  const otherViewMatches = searchQuery
    ? members.filter(m => memberView(m) === otherView).length
    : 0;
  // The page's note about matches past the ticked statuses, for this view's
  // rows only.
  const showingOutsideStatuses =
    showingOutsideFilter &&
    rows.some(m => !statusFilter.includes(memberStatus(m)));

  // Everybody shown who is waiting for an invitation.
  const invitable = useMemo(
    () => rows.filter(m => memberStatus(m) === 'not_invited' && !!m.email),
    [rows]
  );
  const inviteAll = () => {
    const people = invitable;
    const count = people.length;
    setPendingClear({
      title: `Send ${count} invite${count === 1 ? '' : 's'}`,
      message: `Email an invitation to sign in to Roger to the ${
        count === 1 ? 'person' : `${count} people`
      } not invited yet in this list?`,
      warning: INVITE_WARNING,
      confirmLabel: 'Send invites',
      run: () => invite(people),
    });
  };

  const columns = useMemo(
    () =>
      memberColumns({
        canEditUserInfo,
        canEditRogerEmployee,
        showTitles: showEmr,
        showClinics: showEmr,
        clinicSetup: {
          canEdit: canEditClinicSetup && !!boundClinics,
          showClinicRoles,
          boundIds: boundClinics ? boundIds : null,
          clinicOptions: boundClinicOptions,
          clinicCount: boundClinics?.length ?? clinics.length,
          clinicNamesById,
          clinicName,
          roleIds,
          heldThrough: flagHeldThrough,
          saveSetup: requestClinicSetup,
          openSetup: (member, employment) =>
            setSetupOpenFor({ userId: member.workos_user_id, employment }),
        },
        rogerDocLinksByEmail,
        rogerDocAccountsLoaded,
        rogerDocAccountsFailed,
        saveField: (member, patch) => void saveField(member, patch),
        saveTitles: (member, titles) => void saveTitles(member, titles),
        saveCredentials: (member, credentials) =>
          void saveCredentials(member, credentials),
        roleById,
        editAgencyRoles: offeredAgencyRoles
          ? member =>
              setAgencyRolesOpenFor({
                userId: member.workos_user_id,
                before: ticked(member),
              })
          : null,
        licenses: licensesColumn,
        openAppAccounts: member => setAppAccountsOpenFor(member.workos_user_id),
        currentUserId,
        canInvite,
        canDeactivate,
        canCancelInvite,
        ...rowActions,
        onMergeAccounts,
        isBusy,
      }),
    [
      canEditUserInfo,
      canEditRogerEmployee,
      showEmr,
      showClinicRoles,
      canEditClinicSetup,
      boundClinics,
      boundIds,
      boundClinicOptions,
      clinics.length,
      clinicNamesById,
      clinicName,
      roleIds,
      flagHeldThrough,
      requestClinicSetup,
      rogerDocLinksByEmail,
      rogerDocAccountsLoaded,
      rogerDocAccountsFailed,
      saveField,
      saveTitles,
      saveCredentials,
      roleById,
      offeredAgencyRoles,
      ticked,
      licensesColumn,
      currentUserId,
      canInvite,
      canDeactivate,
      canCancelInvite,
      rowActions,
      onMergeAccounts,
      isBusy,
    ]
  );

  const columnFilters = useMemo(() => {
    const filters: Record<string, HeaderFilterConfig> = {
      status: {
        options: memberStatusOptions(view),
        selected: statusFilter,
        onChange: next => onStatusFilterChange(next as MemberStatus[]),
      },
      agency_roles: {
        options: agencyRoleOptions,
        selected: agencyRoleFilter,
        onChange: setAgencyRoleFilter,
      },
    };
    if (showEmr) {
      filters.clinics = {
        options: clinicOptions,
        selected: clinicFilter,
        onChange: setClinicFilter,
      };
    }
    return filters;
  }, [
    view,
    statusFilter,
    onStatusFilterChange,
    agencyRoleOptions,
    agencyRoleFilter,
    showEmr,
    clinicOptions,
    clinicFilter,
  ]);

  // ── Moving to somebody just added ──────────────────────────────────────
  const gridRef = useRef<SpreadsheetHandle>(null);
  const revealedRef = useRef<{ email: string } | null>(null);
  useEffect(() => {
    if (!revealEmail || revealedRef.current === revealEmail) return;
    const added = allMembers.find(
      m => m.email.toLowerCase() === revealEmail.email.toLowerCase()
    );
    if (!added) return;
    revealedRef.current = revealEmail;
    // Nothing may hide them: they may belong to the other view, their status
    // may not be ticked, and a search or a role or clinic filter could too.
    const status = memberStatus(added);
    if (memberView(added) !== view) {
      switchView(memberView(added), status);
    } else if (!statusFilter.includes(status)) {
      onStatusFilterChange([...statusFilter, status]);
    }
    if (searchQuery) onSearchChange('');
    setAgencyRoleFilter([]);
    setClinicFilter([]);
    gridRef.current?.focusCell(added.workos_user_id, 'first_name');
  }, [
    revealEmail,
    allMembers,
    view,
    switchView,
    statusFilter,
    onStatusFilterChange,
    searchQuery,
    onSearchChange,
  ]);

  // ── Hidden columns ─────────────────────────────────────────────────────
  const [hiddenColumns, setHiddenColumnsState] =
    useState<string[]>(loadHiddenColumns);
  const setHiddenColumns = (ids: string[]) => {
    setHiddenColumnsState(ids);
    try {
      localStorage.setItem(HIDDEN_COLUMNS_KEY, JSON.stringify(ids));
    } catch {
      // Private mode or blocked storage: hidden for this visit only.
    }
  };
  const hideableColumns = columns.flatMap(column =>
    column.id &&
    typeof column.header === 'string' &&
    !UNHIDEABLE_COLUMNS.has(column.id)
      ? [{ value: column.id, label: column.header }]
      : []
  );

  // Backspace or Delete on a selected cell. Names are required and the rest
  // is read-only here, so only these clear. Someone not invited yet has only
  // a phone to clear.
  const clearCell = (member: OrgMemberResponse, columnId: string): boolean => {
    switch (columnId) {
      case 'phone':
        if (!canEditUserInfo) return false;
        void saveField(member, { phone_number: null });
        return true;
      case 'titles':
        if (!canEditUserInfo || !showEmr || !isAgencyMember(member))
          return false;
        void saveTitles(member, []);
        return true;
      case 'roger_employee':
        if (!canEditRogerEmployee || !isAgencyMember(member)) return false;
        void saveField(member, { is_roger_employee: false });
        return true;
      default:
        return isAgencyMember(member) && clearClinicCell(member, columnId);
    }
  };

  // Clearing clinics or clinic roles takes access away, so it asks first
  // (see requestClinicSetup).
  const clearClinicCell = (
    member: OrgMemberResponse,
    columnId: string
  ): boolean => {
    if (!editClinicSetup || !boundClinics) return false;
    const entries = setupEntries(member, boundIds);
    switch (columnId) {
      case 'clinics':
        requestClinicSetup(member, []);
        return true;
      case 'clinic_roles': {
        if (!roleIds) return false;
        const none = {
          clinician: false,
          caseManager: false,
          clinicalManager: false,
        };
        requestClinicSetup(
          member,
          entries.map(e => withRoles(e, none, roleIds))
        );
        return true;
      }
      case 'employee_type':
        requestClinicSetup(
          member,
          entries.map(e => withEmployment(e, null))
        );
        return true;
      default:
        return false;
    }
  };

  // A dialog opened from a cell hands the keys back to the grid as it closes,
  // so the cursor carries on from that cell.
  const returnFocusToGrid = useCallback((event: Event) => {
    event.preventDefault();
    gridRef.current?.focus();
  }, []);
  const setupMember = setupOpenFor
    ? rows.find(m => m.workos_user_id === setupOpenFor.userId)
    : undefined;
  const agencyRolesMember = agencyRolesOpenFor
    ? rows.find(m => m.workos_user_id === agencyRolesOpenFor.userId)
    : undefined;
  const appAccountsMember = appAccountsOpenFor
    ? rows.find(m => m.workos_user_id === appAccountsOpenFor)
    : undefined;
  const licensesMember = licensesOpenFor
    ? rows.find(m => m.workos_user_id === licensesOpenFor)
    : undefined;

  const wrapperRef = useRef<HTMLDivElement>(null);
  const maxHeight = useFillHeight(wrapperRef, { bottomInset: 72, min: 200 });
  // A phone has no room for pinned columns: they would cover the rest.
  const isMobile = useIsMobile();

  return (
    <div className='space-y-4'>
      {pendingClear && (
        <GridConfirm
          title={pendingClear.title}
          message={pendingClear.message}
          warning={pendingClear.warning}
          confirmLabel={pendingClear.confirmLabel}
          loading={confirmRunning}
          onAccept={() => {
            const running = pendingClear.run();
            if (!running) {
              setPendingClear(null);
              return;
            }
            setConfirmRunning(true);
            void running.finally(() => {
              setConfirmRunning(false);
              setPendingClear(null);
            });
          }}
          onCancel={() => setPendingClear(null)}
          onCloseAutoFocus={returnFocusToGrid}
        />
      )}
      {setupMember && (
        <ClinicSetupDialog
          key={setupMember.workos_user_id}
          member={setupMember}
          entries={setupEntries(setupMember, boundIds)}
          initialEmployment={setupOpenFor?.employment}
          clinicName={clinicName}
          roleIds={roleIds}
          heldThrough={flagHeldThrough}
          showClinicRoles={showClinicRoles}
          onSave={entries => requestClinicSetup(setupMember, entries)}
          onClose={() => setSetupOpenFor(null)}
          onCloseAutoFocus={returnFocusToGrid}
        />
      )}
      {agencyRolesOpenFor && agencyRolesMember && offeredAgencyRoles && (
        <MemberAgencyRolesDialog
          key={agencyRolesMember.workos_user_id}
          member={agencyRolesMember}
          roles={offeredAgencyRoles}
          selectedIds={agencyRolesOpenFor.before}
          onSave={roleIds => {
            // Shown at once; the save runs behind the closed dialog and puts
            // the roles back with a message if it fails.
            setAgencyRolesOpenFor(null);
            void saveAgencyRoles(
              agencyRolesMember,
              agencyRolesOpenFor.before,
              roleIds
            );
          }}
          onClose={() => setAgencyRolesOpenFor(null)}
          onCloseAutoFocus={returnFocusToGrid}
        />
      )}
      {appAccountsMember && (
        <MemberAppAccountsDialog
          key={appAccountsMember.workos_user_id}
          member={appAccountsMember}
          accounts={rogerDocAccounts}
          onAccountsChange={onRogerDocAccountsChange}
          onChanged={onRogerDocLinksChanged}
          onClose={() => setAppAccountsOpenFor(null)}
          onCloseAutoFocus={returnFocusToGrid}
        />
      )}
      {licensesMember?.roger_user_id && (
        <MemberLicensesDialog
          key={licensesMember.workos_user_id}
          member={{
            ...licensesMember,
            roger_user_id: licensesMember.roger_user_id,
          }}
          canEdit={canEditLicenses}
          onClose={() => {
            setLicensesOpenFor(null);
            void loadLicenseCounts();
          }}
          onCloseAutoFocus={returnFocusToGrid}
        />
      )}
      {/* Team / Not invited sit above Search like other manager tab bars
          (NOA, Orders, OASIS). Search → column chooser → actions stay below. */}
      <div className='mb-3'>
        <ViewToggle
          label='Which members'
          options={[
            { value: 'team', label: 'Team', count: viewCounts.team },
            {
              value: 'not_invited',
              label: 'Not invited',
              count: viewCounts.not_invited,
            },
          ]}
          value={view}
          onChange={next => next !== view && switchView(next)}
        />
      </div>
      {/* Two layouts and nothing in between, chosen by the toolbar's own
          width: one line when everything fits, else the search and buttons
          above the filters. The search gives up width rather than wrapping. */}
      <div className='[container-type:inline-size]'>
        <div className='grid grid-cols-[minmax(0,1fr)_auto] items-end gap-x-2 gap-y-3 [@container(min-width:52rem)]:grid-cols-[minmax(10rem,24rem)_auto_minmax(0,1fr)_auto]'>
          <FilterToolbarSearch
            value={searchQuery}
            onChange={onSearchChange}
            label='Search'
            placeholder='Search by name, email, or phone...'
            // Grow recipe plus grid placement; track width still comes from the
            // container-query columns above. Override sm:min-w-[16rem] so the
            // minmax(0,1fr) / minmax(10rem,24rem) track can shrink without
            // spilling into Invite/Add buttons.
            containerClassName={cn(
              filterToolbarSearchGrowClassName,
              'col-start-1 row-start-1 sm:min-w-0'
            )}
            className='w-full'
          />
          <div className='col-start-1 row-start-2 flex items-center gap-2 self-end pb-0.5 [@container(min-width:52rem)]:col-start-2 [@container(min-width:52rem)]:row-start-1'>
            <ColumnChooser
              label={
                <>
                  <span className='[@container(min-width:52rem)]:hidden'>
                    Columns
                  </span>
                  <span className='hidden [@container(min-width:52rem)]:inline'>
                    Hidden columns
                  </span>
                </>
              }
              options={hideableColumns}
              selected={hiddenColumns}
              onChange={setHiddenColumns}
              heading='Columns to hide'
              clearLabel='Show all'
            />
          </div>
          {exportControl ? (
            <div className='col-start-2 row-start-2 flex items-center justify-end self-end pb-0.5 [@container(min-width:52rem)]:col-start-3 [@container(min-width:52rem)]:row-start-1'>
              {exportControl}
            </div>
          ) : null}
          <div className='col-start-2 row-start-1 flex items-center justify-end gap-2 self-end pb-0.5 [@container(min-width:52rem)]:col-start-4'>
            <SaveStatus saving={savesInFlight > 0} failed={lastSaveFailed} />
            {canInvite && view === 'not_invited' && invitable.length > 0 && (
              <Button
                variant='outline'
                disabled={isLoading}
                onClick={inviteAll}
              >
                <Mail className='h-4 w-4' />
                Invite all ({invitable.length})
              </Button>
            )}
            {canInvite && (
              <Button disabled={isLoading} onClick={onInvite}>
                <UserPlus className='h-4 w-4' />
                Add user
              </Button>
            )}
          </div>
        </div>
      </div>
      {showingOutsideStatuses && (
        <p className='text-sm text-muted-foreground'>
          Some matches are outside the selected statuses, so they are shown too.
        </p>
      )}
      {otherViewMatches > 0 && (
        <p className='text-sm text-muted-foreground'>
          <button
            type='button'
            className='text-primary hover:underline'
            onClick={() => switchView(otherView)}
          >
            {otherViewMatches} {otherViewMatches === 1 ? 'match' : 'matches'} in{' '}
            {otherView === 'team' ? 'Team' : 'Not invited'}
          </button>
        </p>
      )}
      <div ref={wrapperRef}>
        {isLoading ? (
          <div className='flex items-center justify-center py-16'>
            <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
          </div>
        ) : (
          <Spreadsheet
            ref={gridRef}
            data={rows}
            columns={columns}
            getRowId={row => row.workos_user_id}
            // Active members first, then by name.
            initialSorting={INITIAL_SORTING}
            hiddenColumnIds={hiddenColumns}
            // The names stay in view so it is clear whose row is being edited,
            // and the row menu stays in reach.
            pinLeft={isMobile ? 0 : 2}
            pinRight={isMobile ? 0 : 1}
            columnFilters={columnFilters}
            rowClassName={row =>
              row.deactivated_at ? 'bg-slate-50 text-muted-foreground' : ''
            }
            emptyText='No members match.'
            maxHeight={maxHeight}
            canEditRow={canEditProfile}
            onClearCell={clearCell}
          />
        )}
      </div>
    </div>
  );
};
